import React, { useState } from 'react';
import { Button, DecisionFeedback } from '@/components/ui';

// The submit result, shown inline on the entry page where the user pressed Submit (V2-014;
// DESIGN.md STATE-06, DLG-10). It used to open in a dialog; results are inline, and a dialog is
// only for confirming an action or collecting input. Uses the shared DecisionFeedback (V2-013).
//
// The file keeps its name so existing imports and mocks still resolve.

interface SubmissionConfirmationModalProps {
  show: boolean;
  referenceNumber: string;
  attachmentCount: number;
  /** H1: the actual result – 'approved' for the auto-approve (Operations Coordinator) submit. */
  status?: 'pending' | 'approved';
  onClose: () => void;
  onViewApprovals: () => void;
}

const SubmissionConfirmationModal: React.FC<SubmissionConfirmationModalProps> = ({
  show,
  referenceNumber,
  attachmentCount,
  status = 'pending',
  onClose,
  onViewApprovals,
}) => {
  const [copied, setCopied] = useState(false);
  if (!show) return null;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(referenceNumber);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };

  return (
    <section className="tw-rounded-lg tw-border tw-border-line tw-bg-surface tw-p-4 tw-shadow-card" data-testid="submission-result">
      <DecisionFeedback result={{ tone: 'success', message: status === 'approved' ? 'Submitted' : 'Submitted for approval' }} />
      <p className="tw-mb-1 tw-mt-4 tw-text-xs tw-font-semibold tw-tracking-wide tw-text-content-muted">Ticket Number</p>
      <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2">
        <code
          className="tw-rounded tw-border tw-border-line tw-bg-surface-alt tw-px-3 tw-py-2 tw-font-mono tw-text-base tw-font-semibold tw-text-content tw-tabular-nums"
          data-testid="he-reference-number"
          aria-label={`Ticket number ${referenceNumber}`}
        >
          {referenceNumber}
        </code>
        <Button type="button" variant="outline-primary" size="sm" onClick={handleCopy}>
          {copied ? 'Copied' : 'Copy'}
        </Button>
      </div>
      <p className="tw-mb-0 tw-mt-3 tw-text-sm tw-text-content">
        {status === 'approved'
          ? 'Quote this ticket number if you call the office about this submission.'
          : 'Quote this ticket number if you call the office or follow up on approval status.'}
      </p>
      {attachmentCount > 0 && (
        <p className="tw-mb-0 tw-mt-3 tw-text-sm tw-text-content-muted">
          {attachmentCount} attachment{attachmentCount === 1 ? '' : 's'} uploaded to storage.
        </p>
      )}
      <div className="tw-mt-4 tw-flex tw-flex-wrap tw-justify-end tw-gap-2">
        <Button type="button" variant="outline-secondary" onClick={onClose}>
          Done
        </Button>
        <Button type="button" variant="primary" onClick={onViewApprovals}>
          View in Submissions
        </Button>
      </div>
    </section>
  );
};

export default SubmissionConfirmationModal;
